import React from 'react';
import { fmt } from './oilApi';
import { OilIcon } from './OilIcons';

const levelLabel = (lv) => {
  if (!lv) return { txt: 'não construída', cls: 'text-zinc-500' };
  if (lv <= 2) return { txt: 'pequena', cls: 'text-zinc-300' };
  if (lv <= 4) return { txt: 'boa', cls: 'text-amber-300' };
  return { txt: 'monstruosa', cls: 'text-orange-400' };
};

const Row = ({ label, children, testid }) => (
  <div className="flex items-center justify-between gap-2 border-b border-zinc-800/70 py-1 last:border-0"
    data-testid={testid}>
    <span className="shrink-0 text-[9px] font-black uppercase tracking-wider text-zinc-500">{label}</span>
    <span className="text-right text-[11px] font-black leading-tight text-zinc-100">{children}</span>
  </div>
);

export const OilSpyReport = ({ report, services = [], limits = {} }) => {
  if (!report || typeof report !== 'object') return null;
  const ref = levelLabel(report.refinery_level);
  const bot = levelLabel(report.bottling_level);
  const fox = Number(report.fox_dollars || 0);
  const strongest = services.filter((s) => s.offensive)
    .sort((a, b) => (b.damage_pct || 0) - (a.damage_pct || 0))[0];
  const hit = strongest
    ? Math.min(Math.round(fox * (strongest.damage_pct / 100)),
      Number(limits.max_damage_per_attack_fox || Infinity))
    : 0;

  return (
    <div className="mt-3 overflow-hidden rounded-2xl border border-amber-800/80 bg-gradient-to-b from-[#191207] to-black"
      data-testid="oil-spy-report">
      <div className="flex items-center gap-2 border-b border-amber-900/60 bg-black/60 px-3 py-2">
        <span className="h-9 w-9 shrink-0 overflow-hidden rounded-full border border-amber-700/70 bg-black">
          {report.avatar
            ? <img src={report.avatar} alt="" className="h-full w-full object-cover" />
            : <span className="flex h-full w-full items-center justify-center text-base">🦊</span>}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[9px] font-black uppercase tracking-[0.2em] text-amber-500">Dossiê do alvo</p>
          <p className="truncate text-xs font-black text-amber-100">{report.nickname || 'Magnata'}</p>
        </div>
        {report.rank ? (
          <span className="rounded-full border border-amber-700/70 bg-amber-950/50 px-2 py-1 text-[10px] font-black text-amber-300">
            {report.rank}º no ranking
          </span>
        ) : null}
      </div>

      <div className="px-3 py-1">
        <Row label="FOX Dólar" testid="oil-spy-fox">
          <OilIcon k="fox" /> <span className="text-emerald-300">{fmt(fox)}</span>
        </Row>
        <Row label="Black Coins" testid="oil-spy-black">
          <OilIcon k="black" /> <span className="text-amber-300">{fmt(report.black_coins)}</span>
        </Row>
        <Row label="Gotas de petróleo" testid="oil-spy-drops">
          <OilIcon k="drops" /> <span className="text-sky-300">{fmt(report.drops)}</span>
        </Row>
        <Row label="Usina · Envasadora" testid="oil-spy-facilities">
          LV{report.refinery_level} <span className={ref.cls}>({ref.txt})</span>
          {' · '}LV{report.bottling_level} <span className={bot.cls}>({bot.txt})</span>
        </Row>
        <Row label="Integridade das instalações" testid="oil-spy-integrity">
          {[['Usina', report.refinery_integrity], ['Envasadora', report.bottling_integrity]]
            .map(([nome, v], i) => {
              const val = v === undefined || v === null ? 100 : Number(v);
              return (
                <span key={nome}>
                  {i > 0 && ' · '}
                  {nome}{' '}
                  <span className={val <= 0 ? 'text-red-400' : val < 50 ? 'text-orange-300' : 'text-emerald-300'}>
                    {val <= 0 ? 'PARADA' : `${val}/100`}
                  </span>
                </span>
              );
            })}
        </Row>
        <Row label="Segurança" testid="oil-spy-shield">
          {report.has_shield
            ? <span className="text-sky-300">TEM ESCUDO ATIVO</span>
            : <span className="text-red-400">SEM ESCUDO NENHUM</span>}
        </Row>
        {hit > 0 && (
          <Row label="Golpe mais forte tira" testid="oil-spy-damage">
            <span className="text-amber-300">{fmt(hit)}</span> FOX Dólar
          </Row>
        )}
      </div>

      <p className={`px-3 py-1.5 text-[9.5px] font-bold leading-snug ${report.has_shield
        ? 'bg-sky-950/40 text-sky-200' : 'bg-red-950/40 text-red-200'}`}
        data-testid="oil-spy-advice">
        {report.has_shield
          ? 'Protegido: o escudo pode segurar o golpe (mas quebra). Derrube o escudo agora e volte depois.'
          : 'Está na mão: sem escudo, o golpe entra inteiro. Manda o serviço mais forte que o bolso aguenta.'}
      </p>
    </div>
  );
};

export default OilSpyReport;
